import * as React from "react";
import Link from "next/link";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * DESIGN.md §3/§5. Three things changed from v1:
 *  · the fill is `signal` (7.79:1 under its white label) instead of the orange that measured
 *    3.32:1 and failed AA on every button on the site;
 *  · radius is `md` — the pill is reserved for status chips, so a pill now *means* something;
 *  · focus comes from the global `:focus-visible` outline, so it can never go missing the way
 *    it did on the v1 accordion and nav links.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium " +
    "transition-colors duration-150 ease-standard disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-signal text-signal-fg hover:bg-signal-press",
        secondary: "border border-edge-control bg-surface text-ink hover:bg-raised",
        ghost: "text-ink hover:bg-raised",
        // For use inside an inverted band (footer, closing CTA).
        onBand: "bg-band-fg text-band hover:bg-band-fg/90",
        link: "text-signal underline underline-offset-4 hover:text-signal-press",
      },
      size: {
        // 44px — the minimum touch target. v1 had 17–20 controls under 44px per page at 390.
        default: "h-11 px-5 text-body",
        sm: "h-11 px-4 text-caption sm:h-9",
        lg: "h-12 px-7 text-body-l",
        // `link` needs no box.
        none: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  href?: string;
}

export function Button({ className, variant, size, href, children, ...props }: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);

  if (href) {
    const external = /^https?:\/\//.test(href);
    if (external) {
      return (
        <a href={href} className={classes}>
          {children}
        </a>
      );
    }
    return (
      <Link href={href} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <button className={classes} {...props}>
      {children}
    </button>
  );
}

export { buttonVariants };
